import {ArrowType} from "./ArrowType";

/**
 * Index-based arrow definition for config string serialization.
 * Used for export/import of arrows in config strings.
 *
 * @property type - Arrow type (pa, pb, ba, bb, etc.)
 * @property src - Source index (atom or bond index)
 * @property dst - Destination index (atom or bond index)
 * @property p - Anchor atom index (for ba/bb types)
 */
export interface ArrowConfigDef {
    type: ArrowType,
    src: number,
    dst: number,
    p?: number
}

/**
 * ID-based arrow definition for internal storage.
 * Uses opaque string IDs that are stable across workspace transformations.
 * IDs are unique across both atom and bond arrays.
 *
 * @property type - Arrow type (pa, pb, ba, bb, etc.)
 * @property srcId - Source ID (atom or bond ID)
 * @property dstId - Destination ID (atom or bond ID)
 * @property anchorId - Anchor atom ID (for ba/bb types)
 */
export interface ArrowIdDef {
    type: ArrowType,
    srcId: string,
    dstId: string,
    anchorId?: string
}

export const ArrowDef = {
    encode: (defs: ArrowConfigDef[]) => {
      return defs.map(ArrowDef.encodeDef).join(",")
    },
    encodeDef: (def: ArrowConfigDef) => {
        if((def.type === ArrowType.BondToAtom || def.type === ArrowType.BondToBond || def.type === ArrowType.CBondToAtom || def.type === ArrowType.CBondToBond) && def.p !== undefined) {
            // For bond-source arrows, format is ba:src:p:dst or bb:src:p:dst
            return `${def.type}:${def.src}:${def.p}:${def.dst}`
        } else {
            return ArrowDef.createArrowString(def.type, def.src, def.dst)
        }
    },
    createArrowString: (type: ArrowType, src: number, dst: number, p?: number) => {
        let result = `${type}:${src}:${dst}`
        if(p !== undefined) {
            result += `:${p}`
        }
        return result;
    },

    decode: (arrowRep: string) => {
        const components = arrowRep.split(":");
        if (components.length >= 3) {
            const type = components[0];
            if ((type === ArrowType.BondToAtom || type === ArrowType.BondToBond) && components.length === 4) {
                // For bond-source arrows, format is ba:src:p:dst or bb:src:p:dst
                return {
                    type,
                    src: parseInt(components[1]),
                    p: parseInt(components[2]),
                    dst: parseInt(components[3])
                }
            } else if ((type === ArrowType.CBondToAtom || type === ArrowType.CBondToBond) && components.length === 4 ) {
                if(parseInt(components[3]) < 0)
                    return {
                    type,
                    src: parseInt(components[1]),
                    dst: parseInt(components[2]),
                } as ArrowConfigDef
                else return {
                    type,
                    src: parseInt(components[1]),
                    dst: parseInt(components[2]),
                    p: parseInt(components[3]),
                }
            } else {
                // For all arrow types with 3 components (including ba/bb without p parameter)
                return {
                    type,
                    src: parseInt(components[1]),
                    dst: parseInt(components[2]),
                } as ArrowConfigDef
            }
        }
        return { type: ArrowType.Unknown, src: -1, dst: -1};
    },
    decodeArrows: (config: string): ArrowConfigDef[] => {
        const parts = config.split("|");
        const arrowSection = parts.find(part => part.startsWith("ar*"));
        if(!arrowSection) return [];

        const arrowReps = arrowSection.slice(3).split(",");
        return arrowReps.map(ArrowDef.decode)
    },
}